import apiClient from './client'

// Device type interface - now references device_types table
export interface DeviceTypeInfo {
  id: number
  name: string
  code: string
  icon?: string
  color?: string
  description?: string
  sort_order: number
  is_active: boolean
}

export interface Specification {
  id: number
  project_id: number
  electrical_panel_id?: number
  device_type_id?: number
  device_type?: string
  device_type_name?: string
  device_type_code?: string
  device_type_icon?: string
  device_type_color?: string
  name: string
  description?: string
  model?: string
  manufacturer?: string
  unit_price?: number
  power_consumption?: number
  properties?: Record<string, any>
  length_meters?: number
  width?: number
  height?: number
  depth?: number
  visual_template?: string
  documentation_url?: string
  instance_count?: number
  created_at?: string
}

export const getSpecification = async (projectId: number, deviceTypeCode?: string, electricalPanelId?: number): Promise<Specification[]> => {
  const params: Record<string, any> = { project_id: projectId }
  if (deviceTypeCode) {
    params.device_type = deviceTypeCode
  }
  if (electricalPanelId) {
    params.electrical_panel_id = electricalPanelId
  }
  
  const response = await apiClient.get('/api/specification', { params })
  
  // Check if response.data is the array we need (from interceptor)
  if (Array.isArray(response.data)) {
    return response.data
  }
  
  // Fallback for raw response format
  if (response && response.data && response.data.success) {
    return response.data.data
  }
  
  throw new Error('Failed to fetch specification')
}

// Get all specifications from all projects (for copying)
export const getAllSpecifications = async (deviceTypeCode?: string): Promise<Specification[]> => {
  const params: Record<string, any> = {}
  if (deviceTypeCode) {
    params.device_type = deviceTypeCode
  }
  
  const response = await apiClient.get('/api/specification', { params })
  
  // Check if response.data is the array we need (from interceptor)
  if (Array.isArray(response.data)) {
    return response.data
  }
  
  // Fallback for raw response format
  if (response && response.data && response.data.success) {
    return response.data.data
  }
  
  throw new Error('Failed to fetch all specifications')
}

export const getSpecificationItem = async (id: number): Promise<Specification> => {
  const response = await apiClient.get(`/api/specification/${id}`)
  
  // API client interceptor already returns response.data.data directly in response.data
  if (response && response.data && typeof response.data === 'object' && 'id' in response.data && 'name' in response.data) {
    return response.data as unknown as Specification
  }
  
  // Fallback for raw response format (in case interceptor didn't process it)
  if (response && response.data && response.data.success) {
    return response.data.data
  }
  
  throw new Error('Specification item not found')
}

export const createSpecification = async (data: Omit<Specification, 'id' | 'created_at' | 'device_type_name' | 'device_type_code' | 'device_type_icon' | 'device_type_color'> & { device_type?: string; device_type_id?: number }): Promise<Specification> => {
  const response = await apiClient.post('/api/specification', data)
  
  // Check if response.data contains the created ID (from interceptor)
  if (response && response.data && typeof response.data === 'object' && 'id' in response.data) {
    return {
      ...data,
      id: parseInt(response.data.id as string),
      created_at: new Date().toISOString()
    } as Specification
  }
  
  // API client interceptor already returns response.data.data directly
  if (response && typeof response === 'object' && 'id' in response) {
    return {
      ...data,
      id: parseInt(response.id as string),
      created_at: new Date().toISOString()
    } as Specification
  }
  
  // Fallback for raw response format
  if (response && response.data && response.data.success) {
    return {
      ...data,
      id: parseInt(response.data.data.id as string),
      created_at: new Date().toISOString()
    } as Specification
  }
  
  throw new Error('Failed to create specification')
}

export const updateSpecification = async (id: number, data: Partial<Specification>): Promise<void> => {
  await apiClient.put(`/api/specification/${id}`, data)
  
  // API client interceptor handles success response format
  // If no error thrown, operation was successful
}

export const deleteSpecification = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/specification/${id}`)
  
  // API client interceptor handles success response format
  // If no error thrown, operation was successful
}

// Специальные функции для работы с кабелями
export const getCableSpecification = async (projectId: number, electricalPanelId?: number): Promise<Specification[]> => {
  return getSpecification(projectId, 'cable', electricalPanelId)
}

export const createCableSpecification = async (data: {
  project_id: number
  electrical_panel_id?: number
  name: string
  model?: string
  manufacturer?: string
  unit_price?: number
  length_meters?: number
  wire_count: number
  section: number
  cable_type: string
  color_scheme?: string[]
  voltage_rating?: string
  fire_safety?: string
  flexibility?: string
}): Promise<Specification> => {
  const specData: Omit<Specification, 'id' | 'created_at' | 'device_type_name' | 'device_type_code' | 'device_type_icon' | 'device_type_color'> = {
    project_id: data.project_id,
    electrical_panel_id: data.electrical_panel_id,
    device_type_id: 7, // ID для кабелей в device_types
    name: data.name,
    model: data.model,
    manufacturer: data.manufacturer,
    unit_price: data.unit_price,
    length_meters: data.length_meters,
    properties: {
      wire_count: data.wire_count,
      section: data.section,
      cable_type: data.cable_type,
      color_scheme: data.color_scheme,
      voltage_rating: data.voltage_rating,
      fire_safety: data.fire_safety,
      flexibility: data.flexibility
    }
  }
  
  return createSpecification(specData)
}

export interface SpecificationDocument {
  id: number
  specification_id: number
  original_filename: string
  file_size: number
  mime_type: string
  created_at: string
}

export const getSpecificationDocuments = async (specificationId: number): Promise<SpecificationDocument[]> => {
  const response = await apiClient.get(`/api/specification/${specificationId}/documents`)
  if (response && response.data && response.data.success) {
    return response.data.data
  }
  if (Array.isArray(response.data)) {
    return response.data
  }
  throw new Error('Failed to fetch specification documents')
}

export const uploadSpecificationDocuments = async (
  specificationId: number,
  files: FileList | File[]
): Promise<SpecificationDocument[]> => {
  const formData = new FormData()
  for (let i = 0; i < files.length; i++) {
    formData.append('files[]', files[i])
  }

  const response = await apiClient.post(`/api/specification/${specificationId}/documents`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  if (response && response.data && response.data.success) {
    return response.data.data
  }
  if (Array.isArray(response.data)) {
    return response.data
  }
  throw new Error('Failed to upload specification documents')
}

export const deleteSpecificationDocument = async (documentId: number): Promise<void> => {
  await apiClient.delete(`/api/specification-documents/${documentId}`)
} 